import { useState, useEffect, useCallback } from 'react';
import { getAdminLoginHistory } from '../../api/adminApi';
import { fmtIST } from '../../utils/dateFormat';

const cyan = '#00e5ff';

const deviceIcon = (d) => ({ mobile: '📱', tablet: '📲', desktop: '🖥' }[d] || '🖥');

const AdminLoginHistory = () => {
  const [records, setRecords]     = useState([]);
  const [total, setTotal]         = useState(0);
  const [pages, setPages]         = useState(1);
  const [page, setPage]           = useState(1);
  const [search, setSearch]       = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [loading, setLoading]     = useState(true);
  const [isMobile, setIsMobile]   = useState(window.innerWidth < 768);

  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    getAdminLoginHistory({ page, limit: 20, search: search || undefined })
      .then((res) => {
        setRecords(res.data.records || []);
        setTotal(res.data.total || 0);
        setPages(res.data.pages || 1);
      })
      .catch(() => setRecords([]))
      .finally(() => setLoading(false));
  }, [page, search]);

  useEffect(() => { load(); }, [load]);

  const handleSearch = (e) => { e.preventDefault(); setPage(1); setSearch(searchInput); };
  const handleClear  = () => { setSearchInput(''); setSearch(''); setPage(1); };

  return (
    <div>
      <h4 style={{ color: '#fff', fontWeight: 700, marginBottom: 4 }}>User Login History</h4>
      <div style={{ color: '#555', fontSize: 13, marginBottom: 18 }}>{total} total login records</div>

      {/* Search */}
      <form onSubmit={handleSearch} style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
        <input
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search by username or user code..."
          style={{ flex: 1, minWidth: 200, background: 'rgba(0,229,255,0.04)', border: '1px solid rgba(0,229,255,0.2)', borderRadius: 8, padding: '9px 14px', color: '#fff', fontSize: 13, outline: 'none' }}
        />
        <button type="submit" style={{ background: cyan, border: 'none', color: '#000', borderRadius: 8, padding: '9px 20px', fontWeight: 700, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' }}>
          Search
        </button>
        {search && (
          <button type="button" onClick={handleClear} style={{ background: 'rgba(255,255,255,0.06)', border: 'none', color: '#888', borderRadius: 8, padding: '9px 14px', fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' }}>
            Clear
          </button>
        )}
      </form>

      {/* Table */}
      {!isMobile ? (
        <div style={{ background: '#111', border: '1px solid rgba(0,229,255,0.1)', borderRadius: 12, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'rgba(0,229,255,0.07)', borderBottom: '1px solid rgba(0,229,255,0.12)' }}>
                {['#', 'Login Date & Time', 'Username', 'User Code', 'Device', 'Browser', 'OS', 'IP'].map((h) => (
                  <th key={h} style={{ color: cyan, fontSize: 12, fontWeight: 600, padding: '12px 14px', textAlign: 'left', whiteSpace: 'nowrap' }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} style={{ color: '#555', textAlign: 'center', padding: 40, fontSize: 13 }}>Loading...</td></tr>
              ) : records.length === 0 ? (
                <tr><td colSpan={8} style={{ color: '#555', textAlign: 'center', padding: 40, fontSize: 13 }}>No login records found</td></tr>
              ) : (
                records.map((r, i) => (
                  <tr
                    key={r._id}
                    style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(0,229,255,0.03)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  >
                    <td style={{ color: '#555', fontSize: 12, padding: '11px 14px' }}>{(page - 1) * 20 + i + 1}</td>
                    <td style={{ color: '#888', fontSize: 12, padding: '11px 14px', whiteSpace: 'nowrap' }}>{fmtIST(r.createdAt)}</td>
                    <td style={{ padding: '11px 14px' }}>
                      <div style={{ color: '#fff', fontSize: 13, fontWeight: 600 }}>{r.userId?.name || '—'}</div>
                      <div style={{ color: '#aaa', fontSize: 11 }}>@{r.userId?.username || '—'}</div>
                    </td>
                    <td style={{ color: cyan, fontSize: 12, fontFamily: 'monospace', fontWeight: 600, padding: '11px 14px' }}>{r.userId?.userCode || '—'}</td>
                    <td style={{ padding: '11px 14px', whiteSpace: 'nowrap' }}>
                      <span style={{ fontSize: 14 }}>{deviceIcon(r.device)}</span>
                      <span style={{ color: '#888', fontSize: 11, marginLeft: 5, textTransform: 'capitalize' }}>{r.device}</span>
                    </td>
                    <td style={{ color: '#aaa', fontSize: 12, padding: '11px 14px', whiteSpace: 'nowrap' }}>{r.browser || '—'}</td>
                    <td style={{ color: '#aaa', fontSize: 12, padding: '11px 14px', whiteSpace: 'nowrap' }}>{r.os || '—'}</td>
                    <td style={{ color: '#666', fontSize: 11, padding: '11px 14px', fontFamily: 'monospace' }}>{r.ip || '—'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {loading ? (
            <div style={{ color: '#555', textAlign: 'center', padding: 40 }}>Loading...</div>
          ) : records.length === 0 ? (
            <div style={{ color: '#555', textAlign: 'center', padding: 40 }}>No login records found</div>
          ) : (
            records.map((r) => (
              <div key={r._id} style={{ background: '#111', border: '1px solid rgba(0,229,255,0.1)', borderRadius: 12, padding: '14px 16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10, gap: 10 }}>
                  <div>
                    <div style={{ color: '#fff', fontSize: 13, fontWeight: 600 }}>{r.userId?.name || '—'}</div>
                    <div style={{ color: '#aaa', fontSize: 11 }}>@{r.userId?.username || '—'}</div>
                    <div style={{ color: cyan, fontSize: 11, fontFamily: 'monospace', marginTop: 2 }}>{r.userId?.userCode || '—'}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 18 }}>{deviceIcon(r.device)}</div>
                    <div style={{ color: '#555', fontSize: 10, textTransform: 'capitalize' }}>{r.device}</div>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <div>
                    <div style={{ color: '#555', fontSize: 10 }}>Login Time</div>
                    <div style={{ color: '#888', fontSize: 11 }}>{fmtIST(r.createdAt)}</div>
                  </div>
                  <div>
                    <div style={{ color: '#555', fontSize: 10 }}>IP</div>
                    <div style={{ color: '#666', fontSize: 11, fontFamily: 'monospace' }}>{r.ip || '—'}</div>
                  </div>
                  <div>
                    <div style={{ color: '#555', fontSize: 10 }}>Browser</div>
                    <div style={{ color: '#aaa', fontSize: 11 }}>{r.browser || '—'}</div>
                  </div>
                  <div>
                    <div style={{ color: '#555', fontSize: 10 }}>OS</div>
                    <div style={{ color: '#aaa', fontSize: 11 }}>{r.os || '—'}</div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Pagination */}
      {pages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 20, flexWrap: 'wrap' }}>
          {Array.from({ length: Math.min(pages, 5) }, (_, i) => i + 1).map((p) => (
            <button
              key={p}
              onClick={() => setPage(p)}
              style={{
                background: p === page ? cyan : 'rgba(255,255,255,0.06)',
                border: 'none',
                color: p === page ? '#000' : '#888',
                borderRadius: 6,
                padding: '6px 12px',
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {p}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminLoginHistory;
